import { Link } from 'react-router-dom';
import { APP_NAME } from '@teentack/shared';

/**
 * The TeenTack wordmark: a figure with its arms up, "TEEN" in brand orange next
 * to "TACK" in ink, and the tagline set as tiny letterspaced caps beneath.
 *
 * Three surfaces, three variants. `light` is for dark bands (footer): only the
 * text flips, so the mark keeps its orange. `onBrand` is for orange surfaces,
 * where orange-on-orange would vanish, so the whole wordmark goes white.
 */
export function Logo({
  variant = 'dark',
  showTagline = true,
  className = '',
}: {
  variant?: 'dark' | 'light' | 'onBrand';
  showTagline?: boolean;
  className?: string;
}) {
  const onBrand = variant === 'onBrand';
  const light = variant === 'light';
  const accent = onBrand ? 'text-white' : 'text-brand-600';

  return (
    <Link
      to="/"
      className={`inline-flex items-center gap-2.5 rounded-xl ${className}`}
      aria-label={`${APP_NAME} home`}
    >
      <LogoMark className="size-9 shrink-0" />

      <span className="flex flex-col leading-none">
        <span className="text-[1.3rem] font-extrabold tracking-[0.08em]">
          <span className={accent}>TEEN</span>{' '}
          <span className={light || onBrand ? 'text-white' : 'text-ink'}>TACK</span>
        </span>
        {showTagline && (
          <span
            className={`mt-1 text-[0.44rem] font-bold tracking-[0.19em] whitespace-nowrap ${
              light || onBrand ? 'text-white/70' : 'text-ink-soft'
            }`}
          >
            BELIEVE <span className={accent}>•</span> BECOME <span className={accent}>•</span> BE
            THE FUTURE
          </span>
        )}
      </span>
    </Link>
  );
}

/** The figure on its own — used where the wordmark would not fit. */
export function LogoMark({ className = '' }: { className?: string }) {
  return (
    <svg viewBox="0 0 40 40" className={className} aria-hidden="true" focusable="false">
      <circle cx="20" cy="20" r="20" fill="var(--color-brand-50)" />
      {/* Arms thrown up. */}
      <path
        d="M12 15.5 19 20l7-6"
        fill="none"
        stroke="var(--color-brand-600)"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {/* Head. */}
      <circle cx="19.2" cy="11.4" r="3" fill="var(--color-accent-900)" />
      {/* Body and stride. */}
      <path
        d="M19.2 16.5v7.2m0 0-4.4 6.6m4.4-6.6 4.6 6.4"
        fill="none"
        stroke="var(--color-accent-900)"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {/* The spark that doubles as the dot in the wordmark. */}
      <path d="m30 8.5 1 2.6 2.6 1-2.6 1-1 2.6-1-2.6-2.6-1 2.6-1z" fill="var(--color-brand-600)" />
    </svg>
  );
}
